/**
 * PeToTa Business — Payment React Query hooks (B1.8).
 *
 * Thin wrappers around `payment-api.ts` that expose the standard
 * `{ mutate, mutateAsync, isPending, error, reset }` surface used by
 * `PaymentDialog`. The hooks intentionally do NOT invalidate cashier
 * queries — the Cashier feature owns its cache and reacts to the
 * returned `TenderComposerResult` via its own `commitTender` mutation.
 *
 * Boundary: see `features/payment/types.ts` § boundary.
 */

import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import {
  confirm3DS,
  startCardPayment,
  startGiftCardPayment,
  startTransferPayment,
} from './payment-api';
import type {
  Confirm3DSRequest,
  PaymentIntentSnapshot,
  StartCardPaymentRequest,
  StartGiftCardPaymentRequest,
  StartTransferPaymentRequest,
} from './types';

export function useStartCardPayment(
  options?: UseMutationOptions<PaymentIntentSnapshot, Error, StartCardPaymentRequest>,
) {
  return useMutation<PaymentIntentSnapshot, Error, StartCardPaymentRequest>({
    mutationFn: startCardPayment,
    ...options,
  });
}

export function useConfirm3DS(
  options?: UseMutationOptions<PaymentIntentSnapshot, Error, Confirm3DSRequest>,
) {
  return useMutation<PaymentIntentSnapshot, Error, Confirm3DSRequest>({
    mutationFn: confirm3DS,
    ...options,
  });
}

export function useStartGiftCardPayment(
  options?: UseMutationOptions<PaymentIntentSnapshot, Error, StartGiftCardPaymentRequest>,
) {
  return useMutation<PaymentIntentSnapshot, Error, StartGiftCardPaymentRequest>({
    mutationFn: startGiftCardPayment,
    ...options,
  });
}

export function useStartTransferPayment(
  options?: UseMutationOptions<PaymentIntentSnapshot, Error, StartTransferPaymentRequest>,
) {
  return useMutation<PaymentIntentSnapshot, Error, StartTransferPaymentRequest>({
    mutationFn: startTransferPayment,
    ...options,
  });
}
